import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Pencil, MapPinned } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImageUpload from "@/components/ImageUpload";
import MultiImageUpload from "@/components/MultiImageUpload";
import SectionsEditor from "@/components/SectionsEditor";

const CATEGORIES = ["Attraction", "Restaurant", "Bar & Café", "Shopping", "Nature & Walks", "Transport", "Experience"];
const empty = { category: "Attraction", title: "", description: "", image_url: null, gallery: [], sections: [], link: "", address: "", distance: "", lat: "", lng: "" };

export default function NearbyPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/poi").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it, gallery: it.gallery || [], sections: it.sections || [] }); setOpen(true); };

  const save = async () => {
    if (!form.title) return toast.error("Title is required");
    try {
      const payload = { ...form, lat: form.lat === "" ? null : parseFloat(form.lat), lng: form.lng === "" ? null : parseFloat(form.lng) };
      if (editing) await api.put(`/poi/${editing.id}`, payload);
      else await api.post("/poi", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch { toast.error("Could not save"); }
  };

  const del = async (id) => { await api.delete(`/poi/${id}`); toast.success("Removed"); load(); };

  return (
    <div className="space-y-6" data-testid="nearby-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-foreground mb-1">Explore Nearby</h1>
          <p className="text-muted-foreground text-sm">Curate local points of interest — each with its own rich page.</p>
        </div>
        <Button onClick={openNew} data-testid="add-nearby-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-2" /> Add place</Button>
      </div>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-accent" /> : items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-md">
          <MapPinned className="w-8 h-8 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No nearby places yet.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="bg-card border border-border rounded-md overflow-hidden hover-lift" data-testid={`nearby-item-${it.id}`}>
              {it.image_url && <img src={fileUrl(it.image_url)} alt={it.title} className="w-full h-36 object-cover" />}
              <div className="p-4">
                <span className="text-xs uppercase tracking-widest text-accent">{it.category}{it.distance ? ` · ${it.distance}` : ""}</span>
                <h3 className="font-serif text-lg text-foreground mt-1">{it.title}</h3>
                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{it.description}</p>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => openEdit(it)} data-testid={`edit-nearby-${it.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" onClick={() => del(it.id)} data-testid={`delete-nearby-${it.id}`} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? "Edit place" : "Add nearby place"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="nearby-category" className="mt-1.5 w-full h-10 rounded-md border border-input px-3 text-sm bg-background">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Title</Label><Input data-testid="nearby-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Distance</Label><Input data-testid="nearby-distance" value={form.distance} onChange={(e) => setForm({ ...form, distance: e.target.value })} className="mt-1.5" placeholder="5 min walk" /></div>
              <div><Label>Address</Label><Input data-testid="nearby-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Latitude</Label><Input data-testid="nearby-lat" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="mt-1.5" placeholder="51.7601" /></div>
              <div><Label>Longitude</Label><Input data-testid="nearby-lng" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className="mt-1.5" placeholder="-1.8353" /></div>
            </div>
            <div><Label>Short description</Label><Textarea data-testid="nearby-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="mt-1.5" /></div>
            <div><Label>Link (optional)</Label><Input data-testid="nearby-link" value={form.link || ""} onChange={(e) => setForm({ ...form, link: e.target.value })} className="mt-1.5" placeholder="https://" /></div>
            <div><Label className="mb-1.5 block">Cover image</Label><ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testId="nearby-image" /></div>
            <div><Label className="mb-1.5 block">Gallery</Label><MultiImageUpload value={form.gallery} onChange={(v) => setForm({ ...form, gallery: v })} testId="nearby-gallery" /></div>
            <SectionsEditor value={form.sections} onChange={(v) => setForm({ ...form, sections: v })} testId="nearby-sections" />
            <Button onClick={save} data-testid="save-nearby-btn" className="w-full bg-primary text-primary-foreground">Save place</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
